import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { WirePlan } from '../api/types.ts';
import { BrandPin, Icon, type IconName } from '../components/Icon.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { todayString } from '../lib/clock.ts';
import { useDocumentTitle } from '../lib/documentTitle.ts';
import { useIsDesktop } from '../lib/hooks.ts';
import { ownerName, useActivePlan, useStore } from '../state/store.tsx';
import { WelcomeNote } from '../components/WelcomeNote.tsx';

export function Splash() {
  return (
    <div className="splash" role="status" aria-label={t('common.loading')}>
      <BrandPin size={34} />
    </div>
  );
}

/** Skeleton in the shape of the screen, shown while the plan loads. */
export function LoadingState() {
  const desktop = useIsDesktop();
  return (
    <div className={desktop ? 'page loading-desktop' : 'page page--mobile'} aria-busy="true">
      <p className="sr-only" role="status">{t('common.loading')}</p>
      {desktop ? (
        <>
          <div className="skeleton skeleton--header" />
          <div className="loading-desktop__grid">
            <div className="skeleton skeleton--hero" />
            <div className="stack-lg">
              <div className="skeleton skeleton--card" />
              <div className="skeleton skeleton--card-sm" />
              <div className="skeleton skeleton--card" />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="skeleton skeleton--title" />
          <div className="skeleton skeleton--amount" />
          <div className="skeleton skeleton--card" />
          <div className="skeleton skeleton--card-sm" />
          <div className="skeleton skeleton--card" />
        </>
      )}
    </div>
  );
}

export function ErrorState() {
  const { reloadPlan } = useStore();
  return (
    <div className="state-panel" role="alert">
      <span className="state-panel__icon state-panel__icon--amber" aria-hidden="true"><Icon name="alert" size={26} /></span>
      <h1 className="state-panel__title">{t('error.title')}</h1>
      <p className="state-panel__body">{t('error.body')}</p>
      <button type="button" className="button button--primary" onClick={() => void reloadPlan()}>{t('common.retry')}</button>
    </div>
  );
}

/** Start's doors on an empty plan: the same four ways in as /start, each opening there. */
function StartEntry({ onSelf, busy }: { onSelf: () => void; busy: boolean }) {
  const desktop = useIsDesktop();
  const doors: { door: string; icon: IconName; title: MessageKey; body: MessageKey }[] = [
    { door: 'pick', icon: 'check', title: 'startEntry.pick', body: 'startEntry.pickBody' },
    { door: 'drop', icon: 'download', title: 'startEntry.drop', body: 'startEntry.dropBody' },
    { door: 'persona', icon: 'people', title: 'startEntry.persona', body: 'startEntry.personaBody' },
    { door: 'question', icon: 'whatIf', title: 'startEntry.question', body: 'startEntry.questionBody' },
  ];
  if (desktop) [doors[0], doors[1]] = [doors[1]!, doors[0]!];
  return (
    <div className="start-entry" data-testid="start-entry">
      <ul className="start-entry">
        {doors.map((d, i) => (
          <li key={d.door}>
            <Link to={`/start?door=${d.door}`} className={`start-entry__door ${i === 0 ? 'start-entry__door--lead' : ''}`} data-testid={`entry-${d.door}`}>
              <span className="start-entry__icon" aria-hidden="true"><Icon name={d.icon} size={18} /></span>
              <span>
                <span className="start-entry__title">{t(d.title)}</span>
                <span className="start-entry__body">{t(d.body)}</span>
              </span>
              <Icon name="chevronRight" size={18} />
            </Link>
          </li>
        ))}
      </ul>
      <p className="start-entry__more">
        <Link to="/connections" className="text-link">{t('startEntry.assistant')}</Link>
        <button type="button" className="link-button" disabled={busy} onClick={onSelf}>{t('startEntry.self')}</button>
      </p>
    </div>
  );
}

const starterFor = (country: string | undefined): WirePlan => ({
  ...STARTER, configuration: { ...STARTER.configuration, initialBalanceDate: todayString(), ...(country === 'us' || country === 'uk' ? { taxJurisdiction: country } : {}) },
} as WirePlan);

/** The first-run state for an account with no plan: Start, the web onboarding. */
export function EmptyState() {
  const { savePlan, plan } = useStore();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  useDocumentTitle(t('startEntry.title'));
  const addYourself = async () => {
    setBusy(true);
    const ok = await savePlan(starterFor(plan?.configuration.taxJurisdiction), t('plan.summaryText.started'));
    setBusy(false);
    if (ok) navigate('/plan?add=income');
  };
  return (
    <div className="first-run first-run--welcome">
      <WelcomeNote />
      <header className="first-run__head">
        <span className="eyebrow">{t('startEntry.eyebrow')}</span>
        <h1 className="first-run__title">{t('startEntry.title')}</h1>
        <p className="first-run__body">{t('startEntry.body')}</p>
      </header>
      <StartEntry onSelf={() => void addYourself()} busy={busy} />
    </div>
  );
}

export function NotFound() {
  return (
    <div className="state-panel">
      <h1 className="state-panel__title">{t('error.notFound')}</h1>
      <Link to="/" className="button button--primary">{t('error.backHome')}</Link>
    </div>
  );
}

const STARTER: WirePlan = {
  configuration: { initialBalance: '0', timeframeYears: 10, expectedReturnPercent: '6', safeWithdrawalRatePercent: '4' },
  transactions: [], assets: [], properties: [], depreciatingAssets: [], goals: [],
} as unknown as WirePlan;

/** A plan with nothing in it yet: the three ways to fill it. The primary plan keeps EmptyState. */
export function FirstRunState() {
  const { savePlan } = useStore();
  const { plan, canEdit } = useActivePlan();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const name = plan?.name ?? '';
  useDocumentTitle(name || t('firstRun.eyebrow'));
  const addYourself = async () => {
    setBusy(true);
    const ok = await savePlan(STARTER, t('firstRun.summary', { name }));
    setBusy(false);
    if (ok) navigate('/plan?add=income');
  };
  return (
    <div className="first-run">
      <header className="first-run__head">
        <span className="eyebrow">{t('firstRun.eyebrow')}</span>
        <h1 className="first-run__title">{t('firstRun.title', { name })}</h1>
        <p className="first-run__body">{canEdit ? t('firstRun.body') : t('firstRun.viewer', { name: ownerName(plan) ?? t('plans.role.owner') })}</p>
      </header>
      {canEdit && <StartEntry onSelf={() => void addYourself()} busy={busy} />}
    </div>
  );
}

/** Which empty state fits: the first-run ways for a plan made in the app, the example for a first plan. */
export function EmptyPlan() {
  const { plan } = useActivePlan();
  return plan && !plan.isPrimary ? <FirstRunState /> : <EmptyState />;
}
